import { isEqual, isString, uniqWith } from "lodash-es";

import {
  ComparativeAnalyticsData,
  ComparativeAnalyticsFile,
  ComparativeAnalyticsSummary,
  NodeAnalyticsData,
  NodeAnalyticsFile,
  OutcomeDataFile,
} from "src/analytics/api";
import {
  AnalyticsKey,
  FLOW_ANALYTICS_ACTUAL_DISPLAY,
  FLOW_ANALYTICS_BE_ACTUAL_KEY,
  FLOW_ANALYTICS_OUTCOMES_DATA_KEY_PREFIX,
  getAnalyticsKeys,
  getSimulationAnalyticsColumns,
  preprocessAnalyticsData,
  SectionKey,
} from "src/analytics/utils";
import {
  MultiversionTestRunResults,
  MultiversionTestRunVersionResults,
} from "src/api/endpoints";
import { Flow } from "src/api/flowTypes";
import { FlowRunnerResultV2, NodeTestRunResult } from "src/api/types";
import { NodeTypes } from "src/clients/flow-api";
import { SUB_COLUMN_SEPARATOR } from "src/datasets/DatasetTable/utils";

const isArrayOrObject = (value: unknown): boolean =>
  isString(value)
    ? value === "__tktl_array" || value === "__tktl_object"
    : false;
const getOutcomeDataColumns = (outcomesData: OutcomeDataFile["data"]) => {
  return Array.from(new Set(Object.values(outcomesData).flatMap(Object.keys)));
};

const getSomeVersionsErroredDetails = (
  results: MultiversionTestRunVersionResults[],
  flow: Flow,
  isMultiversion: boolean,
): Extract<ComparativeAnalyticsData, { reason: "SOME_VERSION_ERRORED" }> => {
  const erroredVersionNames: string[] = [];

  for (const version of results) {
    if (version.execution_results?.type === "error") {
      const versionName =
        flow.versions.find((v) => v.id === version.id)?.name ?? version.id;
      erroredVersionNames.push(`"${versionName}"`);
    }
  }

  return {
    type: "error",
    reason: "SOME_VERSION_ERRORED",
    erroredVersionNames,
    isMultiversion,
  };
};

const convertVersionName = (
  versionName: string,
  convertActualToDisplay: boolean,
): string => {
  return convertActualToDisplay && versionName === FLOW_ANALYTICS_BE_ACTUAL_KEY
    ? FLOW_ANALYTICS_ACTUAL_DISPLAY
    : versionName;
};

const convertSuccessfulCountByVersion = (
  successfulCountByVersion: Record<string, number>,
  convertActualToDisplay: boolean,
): Record<string, number> => {
  const converted: Record<string, number> = {};
  for (const [versionName, successCount] of Object.entries(
    successfulCountByVersion,
  )) {
    const displayVersionName = convertVersionName(
      versionName,
      convertActualToDisplay,
    );
    converted[displayVersionName] = successCount;
  }
  return converted;
};

const getSimulationAnalyticsSummary = ({
  file,
  convertActualToDisplay,
  ignoredRowsCount: ignoredSimulationRowsCount,
  totalRowsCount,
}: {
  file: ComparativeAnalyticsFile;
  convertActualToDisplay: boolean;
  ignoredRowsCount: number;
  totalRowsCount: number;
}): ComparativeAnalyticsSummary => {
  const summary: ComparativeAnalyticsSummary = {};

  for (const [versionName, successCount] of Object.entries(
    file.successful_count_by_version,
  )) {
    const displayVersionName = convertVersionName(
      versionName,
      convertActualToDisplay,
    );

    summary[displayVersionName] = {
      success_count: successCount,
      // For the actual version considering the ignored rows here does not
      // fully make sense, but we have to handle it that way for now because
      // the successful count does not include rows that got ignored in the
      // simulation. (Currenly a limitation of our simulation analytics query)
      failed_count:
        totalRowsCount - (successCount + ignoredSimulationRowsCount),
      ignored_count: ignoredSimulationRowsCount,
    };
  }

  return summary;
};

export const processSimulationsAnalyticsData = ({
  combinedAnalyticsFile,
  isMultiversion,
  totalRowsCount,
  ignoredRowsCount,
}: {
  combinedAnalyticsFile: ComparativeAnalyticsFile;
  isMultiversion: boolean;
  totalRowsCount: number;
  ignoredRowsCount: number;
}): ComparativeAnalyticsData => {
  // If the intersection of successful rows is empty we exit early
  if (combinedAnalyticsFile.combined_successful_count === 0) {
    return {
      type: "error",
      reason: "NO_SUCCESSFUL_ROWS",
      isMultiversion: isMultiversion,
    };
  }

  // Get the union of all data keys and aux keys
  // so we can use them in the analytics chart
  const analyticsKeysUnion: AnalyticsKey[] = [];

  const originalData = combinedAnalyticsFile.data;

  const convertActualToDisplay = !Object.keys(
    combinedAnalyticsFile.successful_count_by_version,
  ).includes(FLOW_ANALYTICS_ACTUAL_DISPLAY);

  const combinedAnalyticsData = originalData.map((row) => {
    const returnRow = {
      ...row,
      [FLOW_ANALYTICS_OUTCOMES_DATA_KEY_PREFIX]: undefined,
    };
    if (
      convertActualToDisplay &&
      returnRow.__tktl_flow_version_name === FLOW_ANALYTICS_BE_ACTUAL_KEY
    ) {
      returnRow.__tktl_flow_version_name = FLOW_ANALYTICS_ACTUAL_DISPLAY;
    }
    return returnRow;
  });

  const simulationDataColumns = getSimulationAnalyticsColumns(originalData);

  analyticsKeysUnion.push(
    ...getAnalyticsKeys(simulationDataColumns, [], [], {}),
  );

  // Remove duplicates
  const analyticsKeys = uniqWith(analyticsKeysUnion, isEqual).sort((a, b) =>
    a.displayKey.localeCompare(b.displayKey),
  );
  const preprocessedData = preprocessAnalyticsData(
    combinedAnalyticsData,
    analyticsKeys,
  );

  const summary = getSimulationAnalyticsSummary({
    file: combinedAnalyticsFile,
    convertActualToDisplay,
    totalRowsCount: totalRowsCount,
    ignoredRowsCount: ignoredRowsCount,
  });

  const convertedSuccessfulCountByVersion = convertSuccessfulCountByVersion(
    combinedAnalyticsFile.successful_count_by_version,
    convertActualToDisplay,
  );

  return {
    type: "success",
    successful_count_by_version: convertedSuccessfulCountByVersion,
    combined_successful_count: combinedAnalyticsFile.combined_successful_count,
    total_count: totalRowsCount,
    keys: analyticsKeys,
    data: preprocessedData,
    summary,
    isMultiversion: isMultiversion,
  };
};

const getSummary = (
  results: NodeTestRunResult[],
  flow: Flow,
): ComparativeAnalyticsSummary => {
  const summary: ComparativeAnalyticsSummary = {};

  for (const result of results) {
    const versionName =
      flow.versions.find((v) => v.id === result.version_id)?.name ??
      result.version_id;

    summary[versionName] = {
      success_count: result.success_count,
      failed_count: result.failure_count,
      ignored_count: result.ignored_count,
    };
  }

  return summary;
};

export const processTestRunAnalyticsData = (
  combinedAnalyticsFile: ComparativeAnalyticsFile,
  outcomesFile: OutcomeDataFile,
  testRunResult: MultiversionTestRunResults,
  flow: Flow,
): ComparativeAnalyticsData => {
  // All execution results (the output_predecessor and output node of each version)
  // are stored in these arrays for later.
  // We use two different arrays because the output node doesn't have some keys
  // that we'll have in the analytics file (those stripped out by the output schema)
  // and the output predecessor node doesn't have information about failures in previous nodes.
  // If any version was not successful we exit early
  const outputExecutionResults: NodeTestRunResult[] = []; // Used for summary
  const outputPredecessorExecutionResults: NodeTestRunResult[] = []; // Used for analytics keys

  for (const version of testRunResult.flow_versions) {
    if (version.execution_results?.type !== "success") {
      return getSomeVersionsErroredDetails(
        testRunResult.flow_versions,
        flow,
        testRunResult.flow_versions.length > 1,
      );
    }
    outputExecutionResults.push(version.execution_results.nodes["output"]);
    outputPredecessorExecutionResults.push(
      "output_predecessor" in version.execution_results.nodes
        ? version.execution_results.nodes["output_predecessor"]
        : version.execution_results.nodes["output_parent"],
    );
  }

  // If the intersection of successful rows is empty we exit early
  if (combinedAnalyticsFile.combined_successful_count === 0) {
    return {
      type: "error",
      reason: "NO_SUCCESSFUL_ROWS",
      isMultiversion: testRunResult.flow_versions.length > 1,
    };
  }

  // Get the union of all data keys and aux keys
  // so we can use them in the analytics chart
  const analyticsKeysUnion: AnalyticsKey[] = [];

  const originalData = combinedAnalyticsFile.data;
  const originalOutcomeData = outcomesFile.data;

  const combinedAnalyticsData = originalData.map((row) => {
    const outcomeRow = originalOutcomeData[row.__tktl_row_id];

    return {
      ...row,
      [FLOW_ANALYTICS_OUTCOMES_DATA_KEY_PREFIX]: outcomeRow,
    };
  });

  const disabledOutputDataKeys = combinedAnalyticsData.reduce(
    (acc, row) => {
      Object.entries(row).forEach(([key, value]) => {
        if (acc[key] !== true) {
          acc[key] = isArrayOrObject(value);
        }
      });
      return acc;
    },
    {} as Record<string, boolean>,
  );

  const outcomeDataColumns = getOutcomeDataColumns(outcomesFile.data);

  for (const version of outputPredecessorExecutionResults) {
    analyticsKeysUnion.push(
      ...getAnalyticsKeys(
        version.data_columns,
        version.aux_data_columns,
        outcomeDataColumns,
        disabledOutputDataKeys,
      ),
    );
  }

  // Remove duplicates
  const analyticsKeys = uniqWith(analyticsKeysUnion, isEqual).sort((a, b) =>
    a.displayKey.localeCompare(b.displayKey),
  );

  const preprocessedData = preprocessAnalyticsData(
    combinedAnalyticsData,
    analyticsKeys,
  );

  const totalCount = outputExecutionResults[0].total_count;

  const summary = getSummary(outputExecutionResults, flow);

  return {
    type: "success",
    ...combinedAnalyticsFile,
    total_count: totalCount,
    keys: analyticsKeys,
    data: preprocessedData,
    summary,
    isMultiversion: testRunResult.flow_versions.length > 1,
  };
};

const META_FIELDS = ["start_time", "entity_id"] as const;
const META_FIELDS_DISPLAY_KEYS: Record<(typeof META_FIELDS)[number], string> = {
  start_time: "metadata.timestamp",
  entity_id: "metadata.entity_id",
};

export const processNodeAnalyticsData = (
  executionResults: FlowRunnerResultV2 | undefined | null,
  nodeAnalyticsFile: NodeAnalyticsFile,
  outcomesFile: OutcomeDataFile,
  testRunResult: MultiversionTestRunResults | undefined,
  flow: Flow,
  nodeId: string,
): NodeAnalyticsData => {
  if (executionResults?.type !== "success") {
    const flowVersions = testRunResult?.flow_versions ?? [];

    return {
      ...getSomeVersionsErroredDetails(flowVersions, flow, false),
      isMultiversion: false,
    };
  }

  // All execution results (the output_predecessor and output node of each version)
  // are stored in these arrays for later.
  // We use two different arrays because the output node doesn't have some keys
  // that we'll have in the analytics file (those stripped out by the output schema)
  // and the output parent node doesn't have information about failures in previous nodes.
  // If any version was not successful we exit early
  const nodeParentExecutionResults: NodeTestRunResult =
    executionResults.nodes[nodeId];
  const outputPredecessorExecutionResults: NodeTestRunResult =
    "output_predecessor" in executionResults.nodes
      ? executionResults.nodes["output_predecessor"]
      : executionResults.nodes["output_parent"];

  // If no successful rows is empty we exit early
  if (nodeAnalyticsFile.successful_count === 0) {
    return {
      type: "error",
      reason: "NO_SUCCESSFUL_ROWS",
      isMultiversion: false,
    };
  }

  const combinedAnalyticsData = nodeAnalyticsFile.data.map((row) => {
    const outcomeRow = outcomesFile.data[row.row_id] ?? {};
    return {
      ...row,
      outcome_data: outcomeRow,
    };
  });

  const disabledOutputDataKeys = combinedAnalyticsData.reduce<{
    node_data: Record<string, boolean>;
    output_data: Record<string, boolean>;
    aux_data: Record<string, boolean>;
    outcome_data: Record<string, boolean>;
  }>(
    (acc, row) => {
      Object.entries(row.node_data).forEach(([key, value]) => {
        if (acc.node_data[key] !== true) {
          acc.node_data[key] = isArrayOrObject(value);
        }
      });
      Object.entries(row.output_data).forEach(([key, value]) => {
        if (acc.output_data[key] !== true) {
          acc.output_data[key] = isArrayOrObject(value);
        }
      });
      Object.entries(row.aux_data).forEach(([key, value]) => {
        if (acc.aux_data[key] !== true) {
          acc.aux_data[key] = isArrayOrObject(value);
        }
      });
      Object.entries(row.outcome_data).forEach(([key, value]) => {
        if (acc.outcome_data[key] !== true) {
          acc.outcome_data[key] = isArrayOrObject(value);
        }
      });
      return acc;
    },
    {
      node_data: {},
      output_data: {},
      aux_data: {},
      outcome_data: {},
    },
  );

  const analyticsKeysUnion: AnalyticsKey[] = [];

  analyticsKeysUnion.push(
    ...nodeParentExecutionResults.data_columns.map((column) => ({
      displayKey: `data.${column}`,
      actualKey: `node_data.${column}`,
      sectionKey: SectionKey.data,
      disabled: disabledOutputDataKeys.node_data[column],
    })),
  );

  const firstRow = nodeAnalyticsFile.data[0];

  if (
    firstRow.meta.node_type === NodeTypes.SPLIT_2 ||
    firstRow.meta.node_type === NodeTypes.RULE_2 ||
    firstRow.meta.node_type === NodeTypes.DECISION_TABLE
  ) {
    const ruleNodeDisplayKey =
      firstRow.meta.node_type === NodeTypes.RULE_2 ? "Rule" : "Case";
    analyticsKeysUnion.push({
      displayKey:
        firstRow.meta.node_type === NodeTypes.SPLIT_2
          ? "Branch split"
          : ruleNodeDisplayKey,
      actualKey: "meta.case_name",
      sectionKey: SectionKey.node_logic,
      disabled: false,
    });
  }

  const chartingMetaFields = META_FIELDS.filter((key) => firstRow.meta[key]);
  if (chartingMetaFields.length > 0) {
    chartingMetaFields.forEach((key) => {
      analyticsKeysUnion.push({
        displayKey: META_FIELDS_DISPLAY_KEYS[key],
        actualKey: `meta.${key}`,
        sectionKey: SectionKey.other,
        disabled: false,
      });
    });
  }

  // Output node keys
  analyticsKeysUnion.push(
    ...outputPredecessorExecutionResults.data_columns.map((column) => ({
      displayKey: `data.${column}`,
      actualKey: `output_data.${column}`,
      sectionKey: SectionKey.output_data,
      disabled: disabledOutputDataKeys.output_data[column],
    })),
  );

  // Auxiliary data keys
  analyticsKeysUnion.push(
    ...outputPredecessorExecutionResults.aux_data_columns.map((column) => ({
      displayKey: column,
      actualKey: `aux_data.${column}`,
      sectionKey: SectionKey.aux_data,
      disabled: disabledOutputDataKeys.aux_data[column],
    })),
  );

  const outcomesDataColumns = getOutcomeDataColumns(outcomesFile.data);

  // Outcome data keys
  analyticsKeysUnion.push(
    ...outcomesDataColumns.map((column) => ({
      displayKey: column.replace(SUB_COLUMN_SEPARATOR, "."),
      actualKey: `outcome_data.${column}`,
      sectionKey: SectionKey.outcomes,
      disabled: disabledOutputDataKeys.outcome_data[column],
    })),
  );

  const analyticsKeys: AnalyticsKey[] = uniqWith(
    analyticsKeysUnion,
    isEqual,
  ).sort((a, b) => a.displayKey.localeCompare(b.displayKey));

  const preparedData = preprocessAnalyticsData(
    combinedAnalyticsData,
    analyticsKeys,
  );

  return {
    type: "success",
    ...nodeAnalyticsFile,
    keys: analyticsKeys,
    data: preparedData,
    isMultiversion: false,
  };
};
